/**
 * Central navigation permission matrix — single source of truth for
 * BOTH the sidebar visibility (AdminLayout) and the route guards (App.js).
 *
 * Each entry:
 *   - path:  route path
 *   - label: sidebar label
 *   - icon:  Phosphor icon component
 *   - anyOf: array of permission codes; user needs at least one.
 *            Empty/undefined => visible to any authenticated staff user
 *            (auth_only reference data pages).
 *
 * Super Admin bypasses all checks (see rbac.hasPerm).
 *
 * Do NOT hand-edit the AdminLayout nav list or the RequirePermission
 * guards without updating this file — that's how the two get out of
 * sync.
 */
import {
  SquaresFour,
  Buildings,
  CreditCard,
  Truck,
  ArrowsLeftRight,
  ListChecks,
  Receipt,
  ChartBar,
  Handshake,
  UsersThree,
  Stack,
  Percent,
  Target,
  ArrowCounterClockwise,
  Pulse,
  ShieldCheck,
  ClipboardText,
  ChatCircleDots,
  Trash,
} from "@phosphor-icons/react";

export const NAV = [
  { path: "/dashboard",             label: "Dashboard",              icon: SquaresFour,        anyOf: ["dashboard.view"] },
  { path: "/customers",             label: "Customers",              icon: Buildings,          anyOf: ["customers.view"] },
  { path: "/cards",                 label: "Fuel Cards",             icon: CreditCard,         anyOf: ["cards.view"] },
  { path: "/card-change-requests",  label: "Card Change Requests",   icon: ClipboardText,      anyOf: ["card_change_requests.approve", "cards.view"] },
  { path: "/support-tickets",       label: "Support Tickets",        icon: ChatCircleDots,     anyOf: ["support_tickets.view"] },
  { path: "/fleet",                 label: "Fleet",                  icon: Truck,              anyOf: [] /* auth_only */ },
  { path: "/transactions",          label: "Transactions",           icon: ArrowsLeftRight,    anyOf: ["transactions.view"] },
  { path: "/price-lists",           label: "Price Lists",            icon: ListChecks,         anyOf: ["pricing.view"] },
  { path: "/margins",               label: "Margins",                icon: Percent,            anyOf: ["margins.view"] },
  { path: "/fuel-tax-rates",        label: "Fuel Tax Rates",         icon: Percent,            anyOf: ["system_settings.manage"] },
  { path: "/pricing-groups",        label: "Pricing Groups",         icon: Stack,              anyOf: ["pricing.view"] },
  { path: "/invoices",              label: "Invoices",               icon: Receipt,            anyOf: ["accounts_collectable.view"] },
  { path: "/reports",               label: "Reports",                icon: ChartBar,           anyOf: ["financial_reports.download"] },
  { path: "/sales-tools",           label: "Sales Tools",            icon: Target,             anyOf: [] /* auth_only */ },
  { path: "/resumable-imports",     label: "Resumable Imports",      icon: ArrowCounterClockwise, anyOf: ["transactions.upload"] },
  { path: "/jobs",                  label: "Background Jobs",        icon: Pulse,              anyOf: ["system_settings.manage"] },
  { path: "/admin-audit",           label: "Admin Audit",            icon: ShieldCheck,        anyOf: ["audit_log.view"] },
  { path: "/portal-alerts",         label: "Portal Alerts",          icon: ShieldCheck,        anyOf: ["audit_log.view"] },
  { path: "/portal-health",         label: "Portal Health",          icon: ShieldCheck,        anyOf: ["audit_log.view"] },
  { path: "/team-access",           label: "Team Access",            icon: ShieldCheck,        anyOf: ["users.view", "roles.view"] },
  { path: "/profitability",         label: "Profitability",          icon: ChartBar,           anyOf: ["profitability.view"] },
  { path: "/audit-reconciliation",  label: "GP Reconciliation",      icon: ChartBar,           anyOf: ["profitability.view"] },
  { path: "/deleted-records",       label: "Deleted Records",        icon: Trash,              anyOf: ["transactions.restore"] },
  { path: "/dealers",               label: "Dealers",                icon: Handshake,          anyOf: [] /* auth_only */ },
  { path: "/users",                 label: "Users",                  icon: UsersThree,         anyOf: ["users.view"] },
];

// Route → required-perm map, for RequirePermission wrappers. Path prefix
// match: keys are exact route paths but the guard uses string equality
// so nested routes must be listed explicitly if they diverge.
export const ROUTE_PERMS = NAV.reduce((acc, item) => {
  acc[item.path] = item.anyOf;
  return acc;
}, {});

// Add nested routes that share their parent's perm.
ROUTE_PERMS["/customers/:customerId"] = ["customers.view"];
ROUTE_PERMS["/invoices/:invoiceId"] = ["accounts_collectable.view"];
ROUTE_PERMS["/margins/station-overrides"] = ["margins.view"];
ROUTE_PERMS["/sales-tools/analyzer"] = [];
ROUTE_PERMS["/sales-tools/crm"] = [];
ROUTE_PERMS["/sales-tools/crm/:prospectId"] = [];
ROUTE_PERMS["/support-tickets/:ticketId"] = ["support_tickets.view"];
